import { Alert, Box, Typography, Button, Dialog, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, IconButton, Select, MenuItem, TextField, FormControl, InputLabel, Checkbox, FormControlLabel, Chip, Tooltip, Autocomplete } from '@mui/material';
import { Edit as EditIcon, Visibility as VisibilityIcon, VisibilityOff as VisibilityOffIcon } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import * as Yup from 'yup';
import { IPropsBusinessPage } from '../Business';
import { businessEmployeeService, commissionGoalService, finishRequest, feedback, serviceService, productService } from '../../../shared/services';
import { ICommissionGoalView, ICreateCommissionGoal, ICreateCommissionGoalTier, IUpdateCommissionGoal, IUpdateCommissionGoalTier, ICommissionGoalUpdateResult } from '../../../shared/services/api/commissionGoalService';
import { DeleteConfirmationDialog } from '../../../shared/components/SconApp/deleteConfirmation/DeleteConfirmationDialog';
import getValidationErrors from '../../../shared/helpers/getValidationErrors';

// Mapa: formTargetType (general, any-service, etc.) → valor numérico da API
const TARGET_SCOPE_MAP: Record<string, number> = {
  'general': 0,
  'any-product': 1,
  'any-service': 2,
  'specific-product': 3,
  'specific-service': 4,
};

// Mapa: formTargetType → string do enum retornado pelo backend (General, AnyService, etc.)
const TARGET_SCOPE_TO_ENUM: Record<string, string> = {
  'general': 'General',
  'any-product': 'AnyProduct',
  'any-service': 'AnyService',
  'specific-product': 'SpecificProduct',
  'specific-service': 'SpecificService',
};

const tierSchema = Yup.object().shape({
  minValue: Yup.number().typeError('Mín deve ser um número').min(0, 'Mín deve ser >= 0').required('Mín é obrigatório'),
  maxValue: Yup.number().nullable().typeError('Máx deve ser um número').min(0, 'Máx deve ser >= 0'),
  rewardType: Yup.number().min(0).max(2),
  commissionPercent: Yup.number().nullable().typeError('% deve ser um número').min(0, '% deve ser >= 0')
    .when('rewardType', ([rewardType], schema) =>
      rewardType === 0 || rewardType === 2
        ? schema.required('% é obrigatório para este tipo')
        : schema
    ),
  fixedBonus: Yup.number().nullable().typeError('Bônus deve ser um número').min(0, 'Bônus deve ser >= 0')
    .when('rewardType', ([rewardType], schema) =>
      rewardType === 1 || rewardType === 2
        ? schema.required('Bônus é obrigatório para este tipo')
        : schema
    ),
}).test('max-gt-min', 'Máx deve ser maior que Mín', function (value) {
  if (value.maxValue === undefined || value.maxValue === null) return true;
  return value.maxValue! > value.minValue;
});

const goalSchema = Yup.object().shape({
  name: Yup.string().trim().required('Nome é obrigatório'),
  period: Yup.number().typeError('Período deve ser um número').min(1, 'Período deve ser maior que zero').required('Período é obrigatório'),
  tiers: Yup.array().min(1, 'Adicione ao menos um tier').of(tierSchema),
});

function camposCriticosMudaram(old: ICommissionGoalView, formValues: {
  periodDays: number;
  metric: number;
  targetScope: string;
  targetServiceId?: number;
  targetProductId?: number;
  tiers: ICreateCommissionGoalTier[];
}): boolean {
  if (old.periodDays !== formValues.periodDays) return true;
  if ((old.metric === 'Value' ? 1 : 0) !== formValues.metric) return true;
  if (old.targetScope !== formValues.targetScope) return true;
  if ((old.targetServiceId ?? undefined) !== formValues.targetServiceId) return true;
  if ((old.targetProductId ?? undefined) !== formValues.targetProductId) return true;

  // Comparar tiers simplificadamente (quantidade + primeiro tier basta pra detectar mudança)
  if (old.tiers.length !== formValues.tiers.length) return true;
  for (let i = 0; i < old.tiers.length; i++) {
    const ot = old.tiers[i];
    const nt = formValues.tiers[i];
    if (ot.minValue !== nt.minValue || ot.maxValue !== nt.maxValue
      || ot.rewardType !== (nt.rewardType === 1 ? 'FixedBonus' : nt.rewardType === 2 ? 'PercentagePlusBonus' : 'PercentageOnly')
      || ot.commissionPercent !== nt.commissionPercent
      || ot.fixedBonus !== nt.fixedBonus) {
      return true;
    }
  }

  return false;
}

export const BusinessGoals: React.FC<IPropsBusinessPage> = ({ business }) => {
  const [openDialog, setOpenDialog] = useState(false);
  const [editingGoal, setEditingGoal] = useState<ICommissionGoalView | null>(null);
  const [formName, setFormName] = useState('');
  const [formPeriod, setFormPeriod] = useState(30);
  const [formMetric, setFormMetric] = useState<number>(0);
  const [formTargetType, setFormTargetType] = useState<string>('general');
  const [formTargetServiceId, setFormTargetServiceId] = useState<number | undefined>(undefined);
  const [formTargetProductId, setFormTargetProductId] = useState<number | undefined>(undefined);
  const [formTiers, setFormTiers] = useState<ICreateCommissionGoalTier[]>([]);
  const [tierGapsWarning, setTierGapsWarning] = useState<string | null>(null);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  // Fork confirmation state
  const [forkDialogOpen, setForkDialogOpen] = useState(false);
  const [forkName, setForkName] = useState('');

  // Toggle active state (deactivate / reactivate)
  const [toggleGoalTarget, setToggleGoalTarget] = useState<{ goal: ICommissionGoalView; activate: boolean } | null>(null);

  // Show inactive toggle
  const [showInactive, setShowInactive] = useState(false);

  // Employee selection state
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState<number[]>([]);
  const selectedEmployeeIdsRef = useRef<number[]>([]);
  const queryClient = useQueryClient();

  // Keep ref in sync
  useEffect(() => { selectedEmployeeIdsRef.current = selectedEmployeeIds; }, [selectedEmployeeIds]);

  const { data: employees } = useQuery({
    queryKey: ['businessEmployees', business.id],
    queryFn: () => businessEmployeeService.get(business.id),
  });

  const { data: goalEmployees } = useQuery({
    queryKey: ['goalEmployees', editingGoal?.id],
    queryFn: () => commissionGoalService.getGoalEmployees(editingGoal!.id),
    enabled: !!editingGoal,
  });

  // Sync selectedEmployeeIds when goalEmployees loads
  useEffect(() => {
    if (goalEmployees) {
      setSelectedEmployeeIds(goalEmployees.map(e => e.id));
    }
  }, [goalEmployees]);

  const { data: goals, refetch: refetchGoals } = useQuery({
    queryKey: ['goals', business.id, showInactive],
    queryFn: () => commissionGoalService.getCommissionGoals(business.id, !showInactive),
  });

  const { data: services, refetch: refetchServices } = useQuery({
    queryKey: ['services', business.id],
    queryFn: () => serviceService.getByBusiness(business.id, true),
  });

  const { data: products, refetch: refetchProducts } = useQuery({
    queryKey: ['products', business.id],
    queryFn: () => productService.getByBusiness(business.id, true),
  });

  const { mutate: createGoal, isLoading: isCreating } = useMutation(
    (body: ICreateCommissionGoal) => commissionGoalService.createCommissionGoal(body),
    {
      onSuccess: (goalId: number) => {
        if (selectedEmployeeIdsRef.current.length > 0) {
          commissionGoalService.setGoalEmployees(goalId, selectedEmployeeIdsRef.current);
          queryClient.invalidateQueries(['goalEmployees', goalId]);
        }
        finishRequest('Meta criada', 'success', () => { refetchGoals(); handleClose(); });
      },
      onError: (e: Error) => feedback(e.message, 'error'),
    }
  );

  const { mutate: updateGoal, isLoading: isUpdating } = useMutation(
    (body: IUpdateCommissionGoal) => commissionGoalService.updateCommissionGoal(body),
    {
      onSuccess: (result: ICommissionGoalUpdateResult) => {
        const goalId = result.id;
        if (selectedEmployeeIdsRef.current.length > 0) {
          commissionGoalService.setGoalEmployees(goalId, selectedEmployeeIdsRef.current);
          queryClient.invalidateQueries(['goalEmployees', goalId]);
        }
        finishRequest(result.message, 'success', () => { refetchGoals(); handleClose(); });
      },
      onError: (e: Error) => feedback(e.message, 'error'),
    }
  );

  const { mutate: toggleActiveMutation, isLoading: isTogglingActive } = useMutation(
    (body: IUpdateCommissionGoal) => commissionGoalService.updateCommissionGoal(body),
    {
      onSuccess: (_data, variables) => {
        const message = variables.isActive ? 'Meta reativada' : 'Meta desativada';
        finishRequest(message, 'success', () => { refetchGoals(); setToggleGoalTarget(null); });
      },
      onError: (e: Error) => feedback(e.message, 'error'),
    }
  );

  const handleClose = useCallback(() => {
    setOpenDialog(false);
    setEditingGoal(null);
    setFormName('');
    setFormPeriod(30);
    setFormMetric(0);
    setFormTargetType('general');
    setFormTargetServiceId(undefined);
    setFormTargetProductId(undefined);
    setFormTiers([]);
    setForkDialogOpen(false);
    setFormErrors({});
    setTierGapsWarning(null);
    setSelectedEmployeeIds([]);
  }, []);

  const handleEdit = useCallback((goal: ICommissionGoalView) => {
    refetchServices();
    refetchProducts();
    setEditingGoal(goal);
    setFormName(goal.name);
    setFormPeriod(goal.periodDays);
    setFormMetric(goal.metric === 'Value' ? 1 : 0);
    const scopeMap: Record<string, string> = {
      General: 'general',
      AnyProduct: 'any-product',
      AnyService: 'any-service',
      SpecificProduct: 'specific-product',
      SpecificService: 'specific-service',
    };
    setFormTargetType(scopeMap[goal.targetScope] ?? 'general');
    setFormTargetServiceId(goal.targetServiceId ?? undefined);
    setFormTargetProductId(goal.targetProductId ?? undefined);
    setFormTiers(goal.tiers.map(t => ({
      id: t.id,
      minValue: t.minValue,
      maxValue: t.maxValue,
      rewardType: t.rewardType === 'FixedBonus' ? 1 : t.rewardType === 'PercentagePlusBonus' ? 2 : 0,
      commissionPercent: t.commissionPercent,
      fixedBonus: t.fixedBonus,
    })));
    setOpenDialog(true);
  }, [refetchServices, refetchProducts]);

  const detectTierGaps = useCallback((tiers: ICreateCommissionGoalTier[]): string | null => {
    if (tiers.length < 2) return null;

    const sorted = [...tiers].sort((a, b) => a.minValue - b.minValue);

    for (let i = 0; i < sorted.length - 1; i++) {
      const current = sorted[i];
      const next = sorted[i + 1];

      if (current.maxValue === undefined || current.maxValue === null) continue;

      const gapStart = current.maxValue + 1;
      if (next.minValue > gapStart) {
        return `Atenção: há um gap entre ${current.maxValue} e ${next.minValue}. Os valores nessa faixa não são cobertos por nenhum tier — a comissão cairá para a taxa padrão.`;
      }
    }

    return null;
  }, []);

  const handleSave = useCallback(() => {
    // Normalizar tiers: undefined/null minValue → 0
    const normalizedTiers = formTiers.map(t => ({
      ...t,
      minValue: t.minValue ?? 0,
    }));

    // Validar com Yup
    try {
      goalSchema.validateSync({ name: formName, period: formPeriod, tiers: normalizedTiers }, { abortEarly: false });
      setFormErrors({});
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        setFormErrors(getValidationErrors(err));
      }
      return;
    }

    const tiers: ICreateCommissionGoalTier[] = normalizedTiers.map(t => ({
      minValue: t.minValue,
      maxValue: t.maxValue,
      rewardType: t.rewardType,
      commissionPercent: t.commissionPercent ?? undefined,
      fixedBonus: t.fixedBonus ?? undefined,
    }));

    const targetScope = TARGET_SCOPE_MAP[formTargetType] ?? 0;
    const targetServiceId = formTargetType === 'specific-service' ? formTargetServiceId : undefined;
    const targetProductId = formTargetType === 'specific-product' ? formTargetProductId : undefined;

    const gapWarning = detectTierGaps(tiers);
    if (gapWarning) {
      setTierGapsWarning(gapWarning);
      return;
    }
    setTierGapsWarning(null);

    // Verificar ranges sobrepostos
    const sorted = [...tiers].sort((a, b) => a.minValue - b.minValue);
    for (let i = 0; i < sorted.length - 1; i++) {
      if (sorted[i].maxValue !== undefined && sorted[i].maxValue !== null) {
        const overlap = sorted[i + 1].minValue <= sorted[i].maxValue!;
        if (overlap) {
          setFormErrors({ tiers: `Ranges sobrepostos: tier ${i + 1} (${sorted[i].minValue} até ${sorted[i].maxValue}) e tier ${i + 2} (${sorted[i + 1].minValue} em diante).` });
          return;
        }
      }
    }

    // Verificar tiers duplicados
    for (let i = 0; i < tiers.length; i++) {
      for (let j = i + 1; j < tiers.length; j++) {
        const a = tiers[i];
        const b = tiers[j];
        if (a.minValue === b.minValue
          && (a.maxValue ?? null) === (b.maxValue ?? null)
          && a.rewardType === b.rewardType
          && (a.commissionPercent ?? null) === (b.commissionPercent ?? null)
          && (a.fixedBonus ?? null) === (b.fixedBonus ?? null)) {
          setFormErrors({ tiers: `Tiers #${i + 1} e #${j + 1} são idênticos. Remova um deles.` });
          return;
        }
      }
    }

    if (editingGoal) {
      // Verificar se campos críticos mudaram
      const newTargetScopeEnum = TARGET_SCOPE_TO_ENUM[formTargetType] ?? 'General';
      const mudou = camposCriticosMudaram(editingGoal, {
        periodDays: formPeriod,
        metric: formMetric,
        targetScope: newTargetScopeEnum,
        targetServiceId,
        targetProductId,
        tiers,
      });

      if (mudou) {
        // Abrir modal de confirmação de fork
        setForkName(`${editingGoal.name} - Cópia`);
        setForkDialogOpen(true);
        return;
      }

      // Edição segura — chama update direto
      updateGoal({
        id: editingGoal.id,
        name: formName,
        periodDays: formPeriod,
        metric: formMetric,
        targetScope,
        targetServiceId,
        targetProductId,
        isActive: true,
        tiers: tiers as IUpdateCommissionGoalTier[],
      });
    } else {
      createGoal({
        businessId: business.id,
        name: formName,
        periodDays: formPeriod,
        metric: formMetric,
        targetScope,
        targetServiceId,
        targetProductId,
        isActive: true,
        tiers,
      });
    }
  }, [formName, formPeriod, formMetric, formTargetType, formTargetServiceId, formTargetProductId, formTiers, editingGoal, business.id, updateGoal, createGoal, detectTierGaps]);

  const handleForkConfirm = useCallback(() => {
    setForkDialogOpen(false);

    const targetScope = TARGET_SCOPE_MAP[formTargetType] ?? 0;
    const targetServiceId = formTargetType === 'specific-service' ? formTargetServiceId : undefined;
    const targetProductId = formTargetType === 'specific-product' ? formTargetProductId : undefined;

    const tiers: IUpdateCommissionGoalTier[] = formTiers.map(t => ({
      minValue: t.minValue,
      maxValue: t.maxValue,
      rewardType: t.rewardType,
      commissionPercent: t.commissionPercent ?? undefined,
      fixedBonus: t.fixedBonus ?? undefined,
    }));

    // O nome do DTO será sobrescrito pelo backend com "{name} - Cópia",
    // mas vamos mandar o nome personalizado do modal
    updateGoal({
      id: editingGoal!.id,
      name: forkName,
      periodDays: formPeriod,
      metric: formMetric,
      targetScope,
      targetServiceId,
      targetProductId,
      isActive: true,
      tiers,
    });
  }, [editingGoal, forkName, formPeriod, formMetric, formTargetType, formTargetServiceId, formTargetProductId, formTiers, updateGoal]);

  const addTier = () => {
    setFormTiers([...formTiers, { minValue: 0, maxValue: undefined, rewardType: 0, commissionPercent: 0, fixedBonus: 0 }]);
  };

  const updateTier = (index: number, field: string, value: unknown) => {
    const newTiers = [...formTiers];
    newTiers[index] = { ...newTiers[index], [field]: value };
    setFormTiers(newTiers);
  };

  const removeTier = (index: number) => {
    setFormTiers(formTiers.filter((_, i) => i !== index));
  };

  const visibleGoals = goals?.filter(g => showInactive || g.isActive) ?? [];

  return (
    <Box sx={{ p: 2 }}>
      <Box display='flex' flexDirection={{ xs: 'column', sm: 'row' }} justifyContent='space-between' alignItems={{ xs: 'stretch', sm: 'center' }} gap={1} mb={2}>
        <Typography variant='h6'>Metas de Comissão</Typography>
        <Box display='flex' gap={1} alignItems='center' flexWrap='wrap'>
          <FormControlLabel
            control={<Checkbox size='small' checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />}
            label='Mostrar inativos'
          />
          <Button variant='contained' size='small' onClick={() => { refetchServices(); refetchProducts(); setOpenDialog(true); }} sx={{ whiteSpace: 'nowrap' }}>
            Nova Meta
          </Button>
        </Box>
      </Box>

      {!goals || goals.length === 0 ? (
        <Typography variant='body2' color='text.secondary'>Nenhuma meta cadastrada.</Typography>
      ) : visibleGoals.length === 0 ? (
        <Typography variant='body2' color='text.secondary'>Nenhuma meta ativa. Marque &quot;Mostrar inativos&quot; para ver todas.</Typography>
      ) : (
        <TableContainer component={Paper} variant='outlined'>
          <Table size='small' sx={{ minWidth: 600 }}>
            <TableHead>
              <TableRow>
                <TableCell>Nome</TableCell>
                <TableCell>Período (dias)</TableCell>
                <TableCell>Métrica</TableCell>
                <TableCell>Alvo</TableCell>
                <TableCell>Tiers</TableCell>
                <TableCell>Ativo</TableCell>
                <TableCell>Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleGoals.map((goal) => (
                <TableRow key={goal.id}>
                  <TableCell>{goal.name}</TableCell>
                  <TableCell>{goal.periodDays}</TableCell>
                  <TableCell>{goal.metric === 'Quantity' ? 'Quantidade' : 'Valor (R$)'}</TableCell>
                  <TableCell>
                    {goal.targetScope === 'AnyService' ? 'Qualquer serviço' :
                      goal.targetScope === 'AnyProduct' ? 'Qualquer produto' :
                        goal.targetServiceName || goal.targetProductName || 'Geral'}
                  </TableCell>
                  <TableCell>{goal.tiers.length} tiers</TableCell>
                  <TableCell>
                    <Chip label={goal.isActive ? 'Sim' : 'Não'} size='small' color={goal.isActive ? 'success' : 'default'} />
                  </TableCell>
                  <TableCell>
                    <Box display='flex' gap={0.5}>
                      <Tooltip title='Editar'>
                        <IconButton size='small' data-testid={`btn-edit-goal-${goal.id}`} onClick={() => handleEdit(goal)}>
                          <EditIcon fontSize='small' />
                        </IconButton>
                      </Tooltip>
                      {goal.isActive ? (
                        <Tooltip title='Desativar'>
                          <IconButton size='small' color='warning' onClick={() => setToggleGoalTarget({ goal, activate: false })}>
                            <VisibilityOffIcon fontSize='small' />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Tooltip title='Reativar'>
                          <IconButton size='small' color='success' onClick={() => setToggleGoalTarget({ goal, activate: true })}>
                            <VisibilityIcon fontSize='small' />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleClose} maxWidth='md' fullWidth>
        <Box sx={{ p: 3 }}>
          <Typography variant='h6' gutterBottom>
            {editingGoal ? 'Editar Meta' : 'Nova Meta'}
          </Typography>

          <Box display='flex' flexDirection='column' gap={2}>

            {tierGapsWarning && (
              <Alert severity='warning' onClose={() => setTierGapsWarning(null)}>
                {tierGapsWarning}
              </Alert>
            )}

            <TextField label='Nome' value={formName} onChange={(e) => { setFormName(e.target.value); setFormErrors(p => ({ ...p, name: '' })); }} error={!!formErrors.name} helperText={formErrors.name} required />
            <TextField label='Período (dias)' type='number' value={formPeriod} onChange={(e) => { setFormPeriod(Number(e.target.value)); setFormErrors(p => ({ ...p, period: '' })); }} error={!!formErrors.period} helperText={formErrors.period} required />
            <Select value={formMetric} onChange={(e) => setFormMetric(Number(e.target.value))}>
              <MenuItem value={0}>Por quantidade</MenuItem>
              <MenuItem value={1}>Por valor (R$)</MenuItem>
            </Select>

            <FormControl>
              <InputLabel>Alvo</InputLabel>
              <Select value={formTargetType} onChange={(e) => setFormTargetType(e.target.value)} label='Alvo'>
                <MenuItem value='general'>Geral (qualquer serviço/produto)</MenuItem>
                <MenuItem value='any-service'>Qualquer serviço</MenuItem>
                <MenuItem value='any-product'>Qualquer produto</MenuItem>
                <MenuItem value='specific-service'>Serviço específico</MenuItem>
                <MenuItem value='specific-product'>Produto específico</MenuItem>
              </Select>
            </FormControl>

            {formTargetType === 'specific-service' && (
              <FormControl>
                <InputLabel>Serviço</InputLabel>
                <Select value={formTargetServiceId ?? ''} onChange={(e) => setFormTargetServiceId(Number(e.target.value))} label='Serviço'>
                  {services?.map(s => (
                    <MenuItem key={s.id} value={s.id}>{s.description}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {formTargetType === 'specific-product' && (
              <FormControl>
                <InputLabel>Produto</InputLabel>
                <Select value={formTargetProductId ?? ''} onChange={(e) => setFormTargetProductId(Number(e.target.value))} label='Produto'>
                  {products?.map(p => (
                    <MenuItem key={p.id} value={p.id}>{p.description}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {/* Employee selection */}
            {employees && employees.length > 0 && (
              <Box>
                <Typography variant='subtitle2' gutterBottom>Profissionais</Typography>
                <Autocomplete
                  multiple
                  options={employees}
                  getOptionLabel={(emp) => emp.name}
                  value={employees.filter(e => selectedEmployeeIds.includes(e.id))}
                  onChange={(_, newValue) => setSelectedEmployeeIds(newValue.map(e => e.id))}
                  renderInput={(params) => (
                    <TextField {...params} variant='outlined' placeholder='Selecionar profissionais...' />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        size='small'
                        label={option.name}
                        {...getTagProps({ index })}
                        key={option.id}
                      />
                    ))
                  }
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  noOptionsText='Nenhum profissional disponível'
                />
              </Box>
            )}

            <Typography variant='subtitle2'>Tiers</Typography>
            {formTiers.map((tier, i) => (
              <Box key={i} display='flex' gap={1} alignItems='center' flexWrap='wrap'>
                <TextField label='Mín' type='number' sx={{ minWidth: 70, flex: 1, maxWidth: 100 }}
                  value={tier.minValue ?? ''} onChange={(e) => { updateTier(i, 'minValue', e.target.value ? Number(e.target.value) : undefined); setFormErrors(p => ({ ...p, [`tiers[${i}].minValue`]: '' })); }}
                  error={!!formErrors[`tiers[${i}].minValue`]} helperText={formErrors[`tiers[${i}].minValue`]} />
                <TextField label='Máx' type='number' sx={{ minWidth: 70, flex: 1, maxWidth: 100 }}
                  value={tier.maxValue ?? ''} onChange={(e) => { updateTier(i, 'maxValue', e.target.value ? Number(e.target.value) : undefined); setFormErrors(p => ({ ...p, [`tiers[${i}].maxValue`]: '' })); }}
                  error={!!formErrors[`tiers[${i}].maxValue`]} helperText={formErrors[`tiers[${i}].maxValue`]} />
                <Select value={tier.rewardType} onChange={(e) => updateTier(i, 'rewardType', Number(e.target.value))} sx={{ minWidth: 120, flex: 1, maxWidth: 160 }}>
                  <MenuItem value={0}>Percentual</MenuItem>
                  <MenuItem value={1}>Bônus fixo</MenuItem>
                  <MenuItem value={2}>Percentual + Bônus</MenuItem>
                </Select>
                {(tier.rewardType === 0 || tier.rewardType === 2) && (
                  <TextField label='%' type='number' sx={{ minWidth: 60, flex: 1, maxWidth: 90 }}
                    value={tier.commissionPercent !== undefined && tier.commissionPercent !== null ? tier.commissionPercent : ''}
                    onChange={(e) => { updateTier(i, 'commissionPercent', e.target.value === '' ? undefined : Number(e.target.value)); setFormErrors(p => ({ ...p, [`tiers[${i}].commissionPercent`]: '' })); }}
                    error={!!formErrors[`tiers[${i}].commissionPercent`]} helperText={formErrors[`tiers[${i}].commissionPercent`]}
                    InputProps={{ endAdornment: '%' }}
                    inputProps={{ inputMode: 'decimal' }} />
                )}
                {(tier.rewardType === 1 || tier.rewardType === 2) && (
                  <TextField label='R$' placeholder='0,00' sx={{ minWidth: 70, flex: 1, maxWidth: 100 }}
                    value={tier.fixedBonus !== undefined && tier.fixedBonus !== null ? tier.fixedBonus.toFixed(2) : ''}
                    onChange={(e) => {
                      const raw = e.target.value.replace(/[^0-9]/g, '');
                      updateTier(i, 'fixedBonus', raw === '' ? undefined : parseFloat(raw) / 100);
                      setFormErrors(p => ({ ...p, [`tiers[${i}].fixedBonus`]: '' }));
                    }}
                    error={!!formErrors[`tiers[${i}].fixedBonus`]} helperText={formErrors[`tiers[${i}].fixedBonus`]}
                    InputProps={{
                      startAdornment: <Typography variant='body2' sx={{ mr: 0.5, color: 'text.secondary' }}>R$</Typography>,
                    }}
                    inputProps={{ inputMode: 'decimal' }} />
                )}
                <IconButton size='small' onClick={() => removeTier(i)}>✕</IconButton>
              </Box>
            ))}
            {formErrors.tiers && (
              <Typography variant='caption' color='error'>{formErrors.tiers}</Typography>
            )}
            <Button variant='outlined' size='small' onClick={addTier}>+ Adicionar Tier</Button>

            <Box display='flex' gap={1} justifyContent='end'>
              <Button variant='outlined' onClick={handleClose}>Cancelar</Button>
              <Button variant='contained' onClick={handleSave} disabled={isCreating || isUpdating}>{isCreating || isUpdating ? 'Salvando...' : 'Salvar'}</Button>
            </Box>
          </Box>
        </Box>
      </Dialog>

      {/* Fork Confirmation Dialog */}
      <Dialog open={forkDialogOpen} onClose={() => setForkDialogOpen(false)} maxWidth='sm' fullWidth>
        <Box sx={{ p: 3 }}>
          <Typography variant='h6' gutterBottom color='warning.main'>
            ⚠️ Alterar campos críticos criará uma nova meta
          </Typography>

          <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
            Período, métrica, tiers e alvo afetam o cálculo das comissões já realizadas.
          </Typography>

          <Alert severity='info' sx={{ mb: 2 }}>
            <Typography variant='body2'>
              <strong>Ao salvar:</strong>
            </Typography>
            <ul style={{ margin: 4, paddingLeft: 16 }}>
              <li>A meta &quot;{editingGoal?.name}&quot; será <strong>preservada</strong> com os cálculos existentes intactos</li>
              <li>Uma <strong>nova meta</strong> será criada com os novos valores</li>
              <li>A meta antiga ficará <strong>inativa</strong> na lista padrão</li>
              <li>Os funcionários vinculados serão <strong>copiados</strong> automaticamente</li>
            </ul>
          </Alert>

          <TextField
            label='Nome da nova meta'
            value={forkName}
            onChange={(e) => setForkName(e.target.value)}
            size='small'
            fullWidth
            sx={{ mb: 2 }}
          />

          <Box display='flex' gap={1} justifyContent='end'>
            <Button variant='outlined' onClick={() => setForkDialogOpen(false)}>Cancelar</Button>
            <Button variant='contained' color='warning' onClick={handleForkConfirm} disabled={isUpdating}>{isUpdating ? 'Salvando...' : 'Criar Nova Versão'}</Button>
          </Box>
        </Box>
      </Dialog>

      {/* Toggle Active Confirmation Dialog */}
      <DeleteConfirmationDialog
        open={!!toggleGoalTarget}
        isLoading={isTogglingActive}
        label={toggleGoalTarget?.activate
          ? `Tem certeza que deseja reativar a meta "${toggleGoalTarget.goal.name}"?`
          : `Tem certeza que deseja desativar a meta "${toggleGoalTarget?.goal.name}"? Ela continuará existindo para preservar os cálculos já realizados, mas não aparecerá na lista padrão.`}
        onClose={() => setToggleGoalTarget(null)}
        onConfirm={() => {
          if (toggleGoalTarget) {
            const { goal, activate } = toggleGoalTarget;
            const goalTargetScopeMap: Record<string, number> = {
              General: 0,
              AnyProduct: 1,
              AnyService: 2,
              SpecificProduct: 3,
              SpecificService: 4,
            };
            toggleActiveMutation({
              id: goal.id,
              name: goal.name,
              periodDays: goal.periodDays,
              metric: goal.metric === 'Value' ? 1 : 0,
              targetScope: goalTargetScopeMap[goal.targetScope] ?? 0,
              targetProductId: goal.targetProductId ?? undefined,
              targetServiceId: goal.targetServiceId ?? undefined,
              isActive: activate,
              tiers: goal.tiers.map(t => ({
                id: t.id,
                minValue: t.minValue,
                maxValue: t.maxValue,
                rewardType: t.rewardType === 'FixedBonus' ? 1 : t.rewardType === 'PercentagePlusBonus' ? 2 : 0,
                commissionPercent: t.commissionPercent,
                fixedBonus: t.fixedBonus,
              })),
            });
          }
        }}
      />
    </Box>
  );
};
